import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { FaSearch, FaPlus, FaClipboardList, FaFileDownload, FaUsers, FaUserPlus, FaUserCheck, FaEnvelope } from "react-icons/fa";
import styles from "../ClientManagement.module.css";
import { vwClientSummaryService, VWClientSummaryHelper } from "../../services/vwClientSummaryService";
import { useAccessToken } from "../../hooks/useAuth";
import { useAlert } from "../../context/AlertContext";
import ClientStatistics from "./ClientStatistics";
import EditContactModal from "../../components/ui/EditContactModal";

const ClientDashboard = () => {
    const navigate = useNavigate();
    const { getBackendAccessToken } = useAccessToken();
    const { setErrorAlert } = useAlert();
    const [statistics, setStatistics] = useState({
        total: 0,
        active: 0,
        inactive: 0,
        totalDocuments: 0,
        clientsWithDocuments: 0,
        clientsWithoutDocuments: 0,
        avgDocumentsPerClient: '0.0'
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Add Contact modal state
    const [addContactModalOpen, setAddContactModalOpen] = useState(false);

    useEffect(() => {
        loadStatistics();
    }, []);

    const loadStatistics = async () => {
        try {
            setLoading(true);
            setError(null); // Clear any previous errors
            const accessToken = await getBackendAccessToken();
            
            // Load VW client summary which includes all necessary data
            const vwClients = await vwClientSummaryService.getAllClients(accessToken);
            
            // Calculate comprehensive statistics from VW_Client_Summary
            const typeStats = VWClientSummaryHelper.getClientTypeStatistics(vwClients);
            const documentStats = VWClientSummaryHelper.getDocumentStatistics(vwClients);
            
            // Combine statistics
            setStatistics({
                total: typeStats.total,
                active: typeStats.activeClients,
                inactive: typeStats.inactiveClients,
                totalDocuments: documentStats.totalDocuments,
                clientsWithDocuments: documentStats.clientsWithDocuments,
                clientsWithoutDocuments: documentStats.clientsWithoutDocuments,
                avgDocumentsPerClient: documentStats.avgDocumentsPerClient
            });
        } catch (error) {
            console.error('Failed to load client statistics:', error);
            
            // Provide more specific error messages
            let errorMessage = 'Failed to load client statistics';
            
            if (error.message && error.message.includes('500')) {
                errorMessage = 'Server error: Unable to fetch client data. The backend service may be experiencing issues. Please contact your administrator or try again later.';
                console.error('Backend 500 Error Details:', {
                    endpoint: '/api/client-summaries',
                    possibleCauses: [
                        'Database view VW_Client_Summary may not exist',
                        'Database connection issue',
                        'Backend service configuration problem',
                        'Check backend logs for detailed error'
                    ]
                });
            } else if (error.message && error.message.includes('401')) {
                errorMessage = 'Authentication error: Your session may have expired. Please refresh the page.';
            } else if (error.message && error.message.includes('403')) {
                errorMessage = 'Access denied: You do not have permission to view client statistics.';
            } else if (error.message && error.message.includes('404')) {
                errorMessage = 'Service not found: The client summary endpoint is not available.';
            }
            
            setError(errorMessage);
            setErrorAlert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.dashboard}>
            {/* Client Actions */}
            <div className={styles.quickActions}>
                <h3>Client Actions</h3>
                <div className={styles.actionGrid}>
                    <button 
                        className={styles.actionButton}
                        onClick={() => navigate('/clients/onboard')}
                    >
                        <span className={`${styles.actionIcon} ${styles.onboard}`}><FaUserCheck /></span>
                        <span>Onboard Client</span>
                    </button>
                    <button 
                        className={styles.actionButton}
                        onClick={() => navigate('/clients/onboarding-requests')}
                    >
                        <span className={`${styles.actionIcon} ${styles.search}`}><FaEnvelope /></span>
                        <span>Information Requests</span>
                    </button>
                    <button 
                        className={styles.actionButton}
                        onClick={() => navigate('/clients/list')}
                    >
                        <span className={`${styles.actionIcon} ${styles.search}`}><FaClipboardList /></span>
                        <span>View All Clients</span>
                    </button>
                </div>
            </div>

            {/* Contact Actions */}
            <div className={styles.quickActions}>
                <h3>Contact Actions</h3>
                <div className={styles.actionGrid}>
                    <button 
                        className={styles.actionButton}
                        onClick={() => setAddContactModalOpen(true)}
                    >
                        <span className={`${styles.actionIcon} ${styles.userPlus}`}><FaUserPlus /></span>
                        <span>Add New Contact</span>
                    </button>
                    <button 
                        className={styles.actionButton}
                        onClick={() => navigate('/clients/contacts/list')}
                    >
                        <span className={`${styles.actionIcon} ${styles.contacts}`}><FaUsers /></span>
                        <span>View All Contacts</span>
                    </button>
                </div>
            </div>

            {/* Statistics Section */}
            <ClientStatistics 
                statistics={statistics}
                loading={loading}
            />

            {/* Add Contact Modal */}
            <EditContactModal
                contact={null}
                mode="create"
                isOpen={addContactModalOpen}
                onClose={() => setAddContactModalOpen(false)}
                onContactUpdated={() => {
                    setAddContactModalOpen(false);
                    // Optionally refresh statistics if needed
                }}
            />
        </div>
    );
};

export default ClientDashboard;
